import api from '@/lib/api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSession } from 'next-auth/react';
import type { ApiError } from '@/lib/api';
import { chatApiUrl } from '@/lib/chatApiUrl';

export interface ChatMessage {
  _id: string;
  userId: string;
  conversationId: string;
  userMessage: string;
  assistanceMessage: string;
  metadata: Record<string, string>;
  pathway: string;
  isDelete: boolean;
  isPinned: boolean;
  isShared: boolean;
  isLike: boolean;
  isRephrase: boolean;
  feedback: string;
  createdAt: Date;
  updatedAt: Date;
}

interface FeedbackResponse {
  success: boolean;
  chat: ChatMessage;
}

interface DeleteResponse {
  success: boolean;
  deletedId: string;
}

interface FetchConversationArgs {
  conversationId: string;
  page: number;
  limit: number;
  search?: string;
}

interface StreamingMessage {
  event: 'message' | 'error' | 'complete';
  content: string;
  error?: string;
  conversationId: string;
  chatId: string;
  type: string;
}

interface ChatResponse {
  chats: ChatMessage[];
  total: number;
  page: number;
  limit: number;
}
interface ChatState {
  data: ChatResponse;
  loading: boolean;
  error: string | null;
  textDetails: { data: string } | null;
}

const initialState: ChatState = {
  data: {
    chats: [],
    total: 0,
    page: 0,
    limit: 10,
  },
  textDetails: null,
  loading: false,
  error: null,
};

export const likeDislikeChat = createAsyncThunk<
  ChatMessage,
  { chatId: string; like: boolean },
  { rejectValue: string }
>('chat/likeDislike', async ({ chatId, like }, { rejectWithValue }) => {
  try {
    const response = await api.post(
      `${process.env.NEXT_PUBLIC_API_URL}/chat/like/${chatId}`,
      {
        like,
      }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to like/dislike chat'
    );
  }
});

export const updateFeedbackChat = createAsyncThunk<
  FeedbackResponse,
  { chatId: string; feedback: string },
  { rejectValue: string }
>('chat/updateFeedback', async ({ chatId, feedback }, { rejectWithValue }) => {
  try {
    const response = await api.post(
      `${process.env.NEXT_PUBLIC_API_URL}/chat/update-feedback/${chatId}`,
      { feedback }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to update feedback'
    );
  }
});

export const deleteChat = createAsyncThunk<
  DeleteResponse,
  { chatId: string },
  { rejectValue: string }
>('chat/deleteChat', async ({ chatId }, { rejectWithValue }) => {
  try {
    const response = await api.delete(
      `${process.env.NEXT_PUBLIC_API_URL}/chat/delete/${chatId}`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to delete chat'
    );
  }
});

export const getTextFromSpeech = createAsyncThunk<
  { data: string },
  { audioBlob: Blob },
  { rejectValue: string }
>('chat/speech-to-text', async ({ audioBlob }, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.wav'); // Key must match backend field
    const response = await api.post(
      chatApiUrl('/speech-to-text'),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to update feedback'
    );
  }
});

// Async thunk to fetch paginated conversation messages
export const fetchConversation = createAsyncThunk<
  ChatResponse, // return type
  FetchConversationArgs, // argument type
  { rejectValue: string }
>(
  'chat/fetchConversation',
  async ({ conversationId, page, limit, search }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${process.env.NEXT_PUBLIC_API_URL}/chat/list/${conversationId}?page=${page}&limit=${limit}&search=${search ? search : ''}`
      );
      console.warn(response.data.data, '-------axios');
      return response.data.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch conversation'
      );
    }
  }
);

// Example function to use in a React component
export const getAnswerNormally = async (
  query: string,
  conversationId: string,
  isNewConversation: boolean,
  chatId: string,
  referenceHistory: boolean,
  referenceMemory: boolean,
  pathway: string | null,
  onMessage: (msg: StreamingMessage) => void,
  onError?: (error: string) => void
) => {
  try {
    const session = await getSession();
    const response = await fetch(chatApiUrl('/ask'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.accessToken}`,
      },
      body: JSON.stringify({
        query,
        conversationId,
        isNewConversation,
        chatId,
        referenceHistory,
        referenceMemory,
        pathway,
      }),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();
    onMessage(data);
  } catch (error) {
    console.error('Fetch error:', error);
    if (onError)
      onError(error instanceof Error ? error.message : 'Unknown request error');
  }
};

// Non-streaming helper: returns the full response payload once
export const getAnswerOnce = async (
  query: string,
  conversationId: string,
  isNewConversation: boolean,
  chatId: string,
  referenceHistory: boolean,
  referenceMemory: boolean,
  pathway: string | null
) => {
  const { manualRefreshToken } = await import(
    '@/services/refreshTokenService'
  );

  const body = JSON.stringify({
    query,
    conversationId,
    isNewConversation,
    chatId,
    referenceHistory,
    referenceMemory,
    pathway,
  });

  const doRequest = async (accessToken?: string) => {
    const session = await getSession();
    const token = accessToken || session?.accessToken;
    return fetch(chatApiUrl('/ask/stream'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
    });
  };

  // Proactively refresh if access token is near/past expiry
  const session = await getSession();
  const expiresAt = session?.accessTokenExpires ?? 0;
  if (expiresAt && expiresAt - Date.now() < 60_000) {
    await manualRefreshToken();
  }

  let response = await doRequest();

  if (response.status === 401 || response.status === 403) {
    const newToken = await manualRefreshToken();
    if (newToken) {
      response = await doRequest(newToken);
    }
  }

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearChat: (state) => {
      ((state.data = {
        chats: [],
        total: 0,
        page: 0,
        limit: 10,
      }),
        (state.error = null));
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(getTextFromSpeech.pending, (state) => {
        state.error = null;
      })
      .addCase(getTextFromSpeech.fulfilled, (state, action) => {
        state.textDetails = action.payload;
      })
      .addCase(getTextFromSpeech.rejected, (state, action) => {
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export const { clearChat } = chatSlice.actions;
export default chatSlice.reducer;
